"use client";

import React, { useState, useEffect } from "react";
import { RefinanceProvider, useRefinanceContext } from "@/context/RefinanceContext";

import CurrentLoan from "./CurrentLoan";
import RefiLoan from "./RefiLoan";
import HomeInfo from "./HomeInfo";
import TaxesInsurance from "./TaxesInsurance";
import RefinanceResult from "./RefinanceResult";

/* ── tiny helper for dollars ───────────────────────────── */
const toCurrency = (n: number) =>
    `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/* ──────────────────────────────────────────────────────── */
/*   LOCAL INPUT WRAPPER that also updates context          */
/* ──────────────────────────────────────────────────────── */
const Inputs = () => {
    const { setCurrentLoan, setRefiLoan, setHomeInfo, setTaxesInsurance } = useRefinanceContext();

    /* keep the sliders controlled locally so UI is snappy */
    const [current, setCurrent] = useState({
        amount: 0,
        termYears: 0,
        yearsPaid: 1,
        interestRate: 0,
    });
    const [refi, setRefi] = useState({
        amount: 0,
        termYears: 0,
        interestRate: 0,
    });
    const [taxes, setTaxes] = useState({ annualPropertyTax: 0, annualInsurance: 0 });
    const [home, setHome] = useState({ appraisedValue: 0, expectedYears: 1 });

    /* push local -> context whenever something changes */
    useEffect(() => {
        setCurrentLoan(current);
    }, [current, setCurrentLoan]);

    useEffect(() => {
        setRefiLoan(refi);
    }, [refi, setRefiLoan]);

    useEffect(() => {
        setHomeInfo(home);
    }, [home, setHomeInfo]);

    useEffect(() => {
        setTaxesInsurance(taxes);
    }, [taxes, setTaxesInsurance]);

    /* UI – all the same children you had before */
    return (
        <>
            <CurrentLoan
                originalLoan={current.amount}
                setOriginalLoan={(v) => setCurrent({ ...current, amount: v })}
                originalTerm={current.termYears}
                setOriginalTerm={(v) => setCurrent({ ...current, termYears: v })}
                yearsPaid={current.yearsPaid}
                setYearsPaid={(v) => setCurrent({ ...current, yearsPaid: Math.min(v, current.termYears) })}
                interestRate={current.interestRate}
                setInterestRate={(v) => setCurrent({ ...current, interestRate: v })}
            />

            <RefiLoan
                refiLoanAmount={refi.amount}
                setRefiLoanAmount={(v) => setRefi({ ...refi, amount: v })}
                refiTerm={refi.termYears}
                setRefiTerm={(v) => setRefi({ ...refi, termYears: v })}
                refiRate={refi.interestRate}
                setRefiRate={(v) => setRefi({ ...refi, interestRate: v })}
            />

            <HomeInfo
                appraisedValue={home.appraisedValue}
                setAppraisedValue={(v) => setHome({ ...home, appraisedValue: v })}
                expectedYears={home.expectedYears}
                setExpectedYears={(v) => setHome({ ...home, expectedYears: v })}
            />

            <TaxesInsurance
                propertyTax={taxes.annualPropertyTax}
                setPropertyTax={(v) => setTaxes({ ...taxes, annualPropertyTax: v })}
                insurance={taxes.annualInsurance}
                setInsurance={(v) => setTaxes({ ...taxes, annualInsurance: v })}
            />
        </>
    );
};

/* ──────────────────────────────────────────────────────── */
/*   PAGE WRAPPER                                           */
/* ──────────────────────────────────────────────────────── */
const RefinancePayment = () => (
    <RefinanceProvider>
        <div className="py-10 px-4 2xl:pb-60">
            <div
                className=" w-[350px] mx-auto 2xl:max-w-3xl 2xl:mx-auto 2xl:w-[900px] 2xl:pl-[28px]"
                aria-labelledby="refinance-page-title"
            >
                <h1 id="refinance-page-title" className="sr-only">
                    How much will I Save By Refinancing?
                </h1>

                <div className=" mx-auto  mb-4 text-center  2xl:mb-4 2xl:w-[900px]">
                    <h2 className="text-[30px] font-bold uppercase text-[#212459] mb-2 2xl:text-[30px]">
                        How much will I Save By Refinancing?
                    </h2>
                </div>

                {/* all the inputs */}
                <Inputs />

                {/* always render – hook handles the numbers */}
                <RefinanceResult toCurrency={toCurrency} />
            </div>
        </div>
    </RefinanceProvider>
);

export default RefinancePayment;
